import { useDispatch, useSelector } from 'react-redux';

import {
  clearDraft,
  ReportSection,
  selectDraftFor,
} from '@/state/slices/copilot/copilotDraft.ts';

import styles from './CopilotDraftBanner.module.css';

interface Props {
  reportUuid: string;
  section: ReportSection;
  /** Called with the markdown when the doctor accepts the draft. */
  onAccept: (markdown: string) => void;
}

const SECTION_LABEL: Record<ReportSection, string> = {
  findings: 'Findings',
  conclusion: 'Conclusion',
  technique: 'Technique',
  impression: 'Impression',
};

/**
 * Banner shown above a report section's editor when the copilot has staged a
 * draft for that section. The doctor accepts (merging markdown into the editor
 * via onAccept) or rejects (clearing the draft).
 */
export function CopilotDraftBanner({ reportUuid, section, onAccept }: Props) {
  const draft = useSelector(selectDraftFor(reportUuid, section));
  const dispatch = useDispatch();

  if (!draft) return null;

  return (
    <div className={styles.banner} role="region" aria-label="AI draft">
      <div className={styles.header}>
        <span className={styles.title}>
          ECHOGPT · {SECTION_LABEL[section]} draft
        </span>
      </div>
      <div className={styles.preview}>{draft.markdown}</div>
      <div className={styles.actions}>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnGhost}`}
          onClick={() => dispatch(clearDraft({ id: draft.id }))}
        >
          Reject
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={() => {
            onAccept(draft.markdown);
            dispatch(clearDraft({ id: draft.id }));
          }}
        >
          Accept &amp; insert
        </button>
      </div>
    </div>
  );
}
